import React, { useState, useEffect, useContext } from "react";
import { color } from "../../styles";
import { ColumnDiv } from "../../styled/commonStyles";
import { DropdownMultiSelectMenu } from "../DropdownMenu.tsx";
import { FilterOption, FilterType } from "../../../configs/filterTypes";
import { Title } from "./Styled";
import DateFilter from "./DateFilter";
import DateRangeFilter from "./DateRangeFilter";
import PriceRangeFilter from "./PriceRangeFilter";
import StaffFilter from "./StaffFilter";
import { ROLE } from "../../../data/enums";
import CategoryGroupsFilter from "./CategoryGroupsFilter";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";

interface FilterOptionProps {
    title?: string;
    filterType: FilterType;
    addStackFilterOption: Function;
    removeStackFilterOption: Function;
    changeStackFilteredOptions: Function;
    isReset?: boolean;
    stackFilteredOptions?: Record<string, { data: any; type?: string }>;
}

const FilterOptionComponent = ({
    title,
    filterType,
    addStackFilterOption,
    removeStackFilterOption,
    changeStackFilteredOptions,
    isReset,
    stackFilteredOptions = {},
}: FilterOptionProps) => {
    const [selectedItems, setSelectedItems] = useState<FilterOption[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    useEffect(() => {
        if (filterType.field === "storage") {
            if (role === ROLE.FIRST_STORAGE_KEEPER) {
                const first_storage_option = filterType.options.find(opt => opt.value === "Kho 1");
                if (first_storage_option) {
                    setSelectedItems([first_storage_option]);
                    addStackFilterOption(
                        {
                            query: first_storage_option.value,
                            fields: [filterType.field],
                        },
                        filterType
                    );
                }
            }
            else if (role === ROLE.SECOND_STORAGE_KEEPER) {
                const second_storage_option = filterType.options.find(opt => opt.value === "Kho 2");
                if (second_storage_option) {
                    setSelectedItems([second_storage_option]);
                    addStackFilterOption(
                        {
                            query: second_storage_option.value,
                            fields: [filterType.field],
                        },
                        filterType
                    );
                }
            }
        }
    }, [role, filterType.field]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const paramValue = params.get(filterType.field);

        if (paramValue && (filterType.type === "text" || !filterType.type)) {
            const paramValues = paramValue.split(",");
            const matchedItems = filterType.options.filter((option) =>
                paramValues.includes(option.value)
            );
            setSelectedItems(matchedItems);
            matchedItems.forEach((item) => {
                addStackFilterOption(
                    {
                        query: item.value,
                        fields: [filterType.field],
                    },
                    filterType
                );
            });
            setIsInitialized(true);
        }
    }, []);

    useEffect(() => {
        if (isReset) {
            setSelectedItems([]);
            setIsInitialized(false);
        }
    }, [isReset]);

    const handleOnclickItem = (item: FilterOption, isAdded: boolean) => {
        setIsInitialized(true);
        if (isAdded) {
            setSelectedItems((prev) => [...prev, item]);
            addStackFilterOption(
                {
                    query: item.value,
                    fields: [filterType.field],
                },
                filterType
            );
        } else {
            setSelectedItems((prev) => {
                const newItems = prev.filter((i) => i.value !== item.value);
                if (newItems.length === 0) {
                    setIsInitialized(false);
                }
                return newItems;
            });
            removeStackFilterOption(
                {
                    query: item.value,
                    fields: [filterType.field],
                },
                filterType
            );
        }
    };

    const getMenuText = () => {
        if (selectedItems.length === 0) {
            return filterType.text;
        }
        return selectedItems.map((item) => item.text).join(", ");
    };

    // Helper function to determine staff role based on field name
    const getStaffRoleForField = (field: string): ROLE | null => {
        switch (field) {
            case "sale_staff":
                return ROLE.SALE_STAFF;
            case "logistic_staff":
                return ROLE.LOGISTIC_STAFF;
            default:
                return null;
        }
    };

    /** Single date: narrow column (~DD/MM/YYYY); avoid flexShrink:0 + minWidth forcing row overflow */
    const DATE_FILTER_COL_PX = 125;

    const getMaxWidth = () => {
        if (filterType.type === "date_range_utc" || filterType.type === "date_range") {
            return "300px";
        }
        if (filterType.type === "date") {
            return `${DATE_FILTER_COL_PX}px`;
        }
        return "200px";
    };

    const getWidth = () => {
        if (filterType.type === "date_range_utc" || filterType.type === "date_range") {
            return "300px";
        }
        if (filterType.type === "date") {
            return `${DATE_FILTER_COL_PX}px`;
        }
        return "auto";
    };

    return (
        <>
            {!(
                filterType.field === "storage" &&
                (role === ROLE.FIRST_STORAGE_KEEPER || role === ROLE.SECOND_STORAGE_KEEPER)
            ) && (
                <ColumnDiv
                    gap="4px"
                    style={{
                        minWidth: filterType.type === "date" ? 0 : "100px",
                        maxWidth: getMaxWidth(),
                        flexShrink: filterType.type === "date" ? 1 : 0,
                        flexBasis: filterType.type === "date" ? `${DATE_FILTER_COL_PX}px` : undefined,
                        width: getWidth(),
                    }}
                >
                    <Title>{title}</Title>
                    {(filterType.type === "date_range_utc" || filterType.type === "date_range") && (
                        <DateRangeFilter
                            onChangeValue={(value: any) => {
                                changeStackFilteredOptions(value, filterType);
                            }}
                            isReset={isReset}
                        />
                    )}
                    {filterType.type === "date" && (
                        <DateFilter
                            value={stackFilteredOptions[filterType.field]?.data ?? null}
                            onChangeValue={(value) => {
                                changeStackFilteredOptions(value, filterType);
                            }}
                        />
                    )}
                    {filterType.type === "staff" && (
                        <StaffFilter
                            onChangeValue={(value: any) => {
                                changeStackFilteredOptions(value, filterType);
                            }}
                            isReset={isReset}
                            filterType={filterType}
                            staffRole={getStaffRoleForField(filterType.field)!}
                        />
                    )}
                    {(filterType.type === "text" ||
                        (!filterType.type && filterType.field !== "category_group")) && (
                        <DropdownMultiSelectMenu
                            menuList={filterType.options}
                            menuText={getMenuText()}
                            menuField={filterType.field}
                            handleOnclickItem={handleOnclickItem}
                            style={{
                                height: "30px",
                                fontSize: "12px",
                                color: color.secondary.DARK_GREY,
                            }}
                            isReset={isReset}
                            dataTestId={filterType.dataTestId}
                        />
                    )}
                    {filterType.field === "category_group" && (
                        <CategoryGroupsFilter
                            onChangeValue={(value: any) => {
                                changeStackFilteredOptions(value, filterType);
                            }}
                            isReset={isReset}
                            filterType={filterType}
                        />
                    )}
                    {filterType.type === "price_range" && (
                        <PriceRangeFilter
                            menuText={filterType.text}
                            changeStackFilteredOptions={changeStackFilteredOptions}
                            filterType={filterType}
                        />
                    )}
                </ColumnDiv>
            )}
        </>
    );
};

export default FilterOptionComponent;
